/** Shared file-tree filter utilities used by WorkspaceExplorer, LinkedFolderTree, and FilePickerDialog. */
import { isProjectFolder } from '@papan/types'

/** Minimal node shape required by the filter functions. */
export interface FilterableNode {
  name: string
  isFolder: boolean
  children?: FilterableNode[]
  isLoaded?: boolean
}

/** Match a full path against a filter pattern.
 *  `*` matches one path segment (anything except `/`).
 *  `**` matches one or more segments (anything including `/`).
 *  When anchored (leading `/`), the pattern must match from the start of the path.
 *  Otherwise it's a substring match. */
export function pathMatchesFilter(path: string, filter: string, anchored: boolean): boolean {
  if (!filter.includes('*')) {
    return anchored ? path.startsWith(filter) : path.includes(filter)
  }
  const escaped = filter.replace(/[.+^${}()|[\]\\]/g, '\\$&')
  // Replace ** before * — use placeholder to avoid double-replace
  const withGlobs = escaped.replace(/\*\*/g, '\x00').replace(/\*/g, '[^/]*').replace(/\x00/g, '.+')
  const re = anchored ? new RegExp('^' + withGlobs) : new RegExp(withGlobs)
  return re.test(path)
}

/** Check if a node (or any loaded descendant) matches the filter against its full path.
 *  pathPrefix is the parent's full path (e.g. "/" for workspace root, "papan (root)/" for linked). */
export function nodeMatchesFilter(node: FilterableNode, filter: string, pathPrefix: string, anchored: boolean): boolean {
  const name = node.name.toLowerCase()
  if (node.isFolder) {
    const folderPath = pathPrefix + name + '/'
    if (pathMatchesFilter(folderPath, filter, anchored)) return true
    if (!node.isLoaded) {
      if (anchored && !filter.includes('*')) return filter.startsWith(folderPath)
      return true // might have matching descendants
    }
    if (node.children) {
      return node.children.some(child => nodeMatchesFilter(child, filter, folderPath, anchored))
    }
    return false
  }
  return pathMatchesFilter(pathPrefix + name, filter, anchored)
}

/** Sort priority: folders first (excluding project folders), then project folders, then files.
 *  Within each group, preserve existing order (typically alphabetical from API). */
export function fileSortKey(item: { name: string; isFolder: boolean }): number {
  if (isProjectFolder(item.name)) return 1
  if (item.isFolder) return 0
  return 2
}

/** Sort an array of file items: folders > project folders > files. Stable sort preserves name order within groups. */
export function sortFileItems<T extends { name: string; isFolder: boolean }>(items: T[]): T[] {
  return [...items].sort((a, b) => fileSortKey(a) - fileSortKey(b))
}
